import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { Check, Loader2, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  ChannelHint,
  ResolvedChannelValue,
} from "@/types/chat-external-message";
import { labelForHint } from "./utils/channelLabels";

export type ComposerSendIndicatorState =
  | { kind: "idle" }
  | { kind: "sending"; hint: ChannelHint }
  | { kind: "sent"; resolved: ResolvedChannelValue }
  | { kind: "warning" };

interface ComposerSendStatusIndicatorProps {
  state: ComposerSendIndicatorState;
}

const labelForResolved = (
  resolved: ResolvedChannelValue,
  t: (key: string, opts?: Record<string, string>) => string,
): string => {
  if (resolved === "STREAM_ONLY") {
    return t("communicationChannels.composer.status.sentStreamOnly");
  }
  return t(`communicationChannels.channels.${resolved}.label`);
};

const BASE_CLASS =
  "mt-2 inline-flex items-center gap-2 rounded-md px-2.5 py-1 text-xs";

export const ComposerSendStatusIndicator: FC<
  ComposerSendStatusIndicatorProps
> = ({ state }) => {
  const { t } = useTranslation();

  if (state.kind === "idle") return null;

  if (state.kind === "sending") {
    return (
      <div
        className={cn(BASE_CLASS, "bg-slate-50 text-slate-600")}
        role="status"
        aria-live="polite"
      >
        <Loader2 className="size-3.5 animate-spin" aria-hidden />
        <span>
          {t("communicationChannels.composer.status.sending", {
            channel: labelForHint(state.hint, t),
          })}
        </span>
      </div>
    );
  }

  if (state.kind === "sent") {
    return (
      <div
        className={cn(BASE_CLASS, "bg-green-100 text-green-800")}
        role="status"
        aria-live="polite"
      >
        <Check className="size-3.5" aria-hidden />
        <span>
          {state.resolved === "STREAM_ONLY"
            ? labelForResolved(state.resolved, t)
            : t("communicationChannels.composer.status.sent", {
                channel: labelForResolved(state.resolved, t),
              })}
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(BASE_CLASS, "bg-destructive/5 text-destructive")}
      role="status"
      aria-live="polite"
    >
      <TriangleAlert className="size-3.5" aria-hidden />
      <span>{t("communicationChannels.composer.status.fallback")}</span>
    </div>
  );
};
